import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';

// Interface for company data
export interface Company {
  id: string;
  name: string;
  website?: string;
  sponsorshipBadge?: string;
  // SSO configuration
  allowedEmailDomains?: string[]; // Email domains allowed for Microsoft SSO (e.g., ['company.com', 'subsidiary.com'])
  // Legacy address fields
  address_street?: string;
  address_house_number?: string;
  address_zip_code?: string;
  address_city?: string;
  address_country?: string;
  // Legacy fields for backward compatibility
  number?: string;
  court?: string;
  legal_form?: string;
  isInvestor?: boolean;
  availableProjects?: string[];
  [key: string]: any; // Allow additional properties
}

export const useCompanies = (autoFetch: boolean = true) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const companiesCollection = collection(db, 'companies');
      const companiesSnapshot = await getDocs(companiesCollection);
      
      const companiesData: Company[] = [];
      companiesSnapshot.forEach((doc) => {
        const data = doc.data();
        companiesData.push({
          id: doc.id,
          ...data
        } as Company);
      });
      
      // Sort companies by name
      companiesData.sort((a, b) => a.name.localeCompare(b.name));
      
      setCompanies(companiesData);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ useCompanies: Error fetching companies:', err);
      }
      setError('Failed to load companies');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Only fetch companies when hook is first used if autoFetch is true
  useEffect(() => {
    if (autoFetch) {
      fetchCompanies();
    }
  }, [fetchCompanies, autoFetch]);

  const findCompanyByEmployeeCode = useCallback(async (employeeCode: string): Promise<Company | null> => {
    try {
      // Call the Cloud Function to verify the employee registration code
      const response = await fetch('https://europe-west3-thescon.cloudfunctions.net/verifyEmployeeRegistrationCode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: employeeCode }),
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      if (!data.success) {
        return null;
      }

      // Return the company data from the Cloud Function response
      const companyData = {
        id: data.companyId,
        name: data.companyName,
        sponsorshipBadge: data.sponsorshipBadge
      } as Company;
      
      return companyData;
    } catch (error) {
      return null;
    }
  }, []);

  return {
    companies,
    loading,
    error,
    refetch: fetchCompanies,
    findCompanyByEmployeeCode
  };
};
